import { Tooltip, TooltipTrigger, Button, TooltipContent, Text, IconName } from '@flatfile/baseline'
import { Flatfile } from '@flatfile/api-internal'

export const ICON_SIZE = 20
export const iconMap: { [key: string]: IconName } = {
  agent: 'people',
  space: 'rocket',
  document: 'folder-2',
  workbook: 'book',
  sheet: 'file-text',
  snapshot: 'camera-1',
  records: 'bullet-list',
  file: 'files',
  job: 'truck',
  program: 'layout-grid-2',
  commit: 'cmd-box',
  layer: 'square-behind-square-1',
  secret: 'key-2',
  cron: 'calendar-1',
  environment: 'earth',
  action: 'console',
  'data-clip': 'data-clip',
  default: 'console',
}

export const eventList = Object.values(Flatfile.EventTopic)

export const eventTopics = eventList.reduce((acc: string[], event) => {
  const [group] = event.split(':')
  if (!acc.includes(group)) {
    acc.push(group)
  }
  return acc
}, [])

const agentEventTopics = (topics: string[]) => {
  return topics.reduce((mapped: { [key: string]: string[] }, topic) => {
    const [prefix, action] = topic.split(':')
    mapped[prefix] = mapped[prefix] ? [...mapped[prefix], action] : [action]
    return mapped
  }, {})
}

export type Agent = {
  slug: string
  title: string
  description: string
  learn: string
  link: string
  topics: string[]
  dist: string
}

export type GroupType = {
  name: string
  about: string
  agents: Agent[]
}

const extractors: Agent[] = [
  {
    slug: 'xlsx-extractor',
    title: 'XLSX Extractor',
    description: 'Automatically extracts data from Excel files',
    learn: 'Learn more about the plugin deployed in this agent',
    link: 'https://flatfile.com/plugins/plugin-xlsx-extractor',
    topics: ['file:created', 'job:ready'],
    dist: 'https://raw.githubusercontent.com/FlatFilers/agents/main/xlsx-extractor/dist/index.js',
  },
  {
    slug: 'json-extractor',
    title: 'JSON Extractor',
    description: 'Automatically extracts data from JSON files',
    learn: 'Learn more about the plugin deployed in this agent',
    link: 'https://flatfile.com/plugins/plugin-json-extractor',
    topics: ['file:created', 'job:ready'],
    dist: 'https://raw.githubusercontent.com/FlatFilers/agents/main/json-extractor/dist/index.js',
  },
  {
    slug: 'xml-extractor',
    title: 'XML Extractor',
    description: 'Automatically extracts data from XML files',
    learn: 'Learn more about the plugin deployed in this agent',
    link: 'https://flatfile.com/plugins/plugin-xml-extractor',
    topics: ['file:created', 'job:ready'],
    dist: 'https://raw.githubusercontent.com/FlatFilers/agents/main/xml-extractor/dist/index.js',
  },
]

const demos: Agent[] = [
  {
    slug: 'demo',
    title: 'Demo',
    description: 'Powers dashboard example apps',
    learn: 'See the demos in action',
    link: '',
    topics: ['job:ready', 'commit:created', 'file:created', 'job:created', 'job:completed'],
    dist: 'https://raw.githubusercontent.com/FlatFilers/demo-agent/main/dist/demo.js',
  },
]

export const library: GroupType[] = [
  {
    name: 'Extractors',
    about: 'Extractors are agents that automatically extract data from files',
    agents: extractors,
  },
  {
    name: 'Demos',
    about: 'Demo agents power dashboard example apps',
    agents: demos,
  },
]
export const libraryGroups = library.map((g) => g.name)
export const teasers = library.map((g) => g.agents).flat()

export const getAgentIconSet = (topics: string[]): JSX.Element[] => {
  const agentTopicList = agentEventTopics(eventList)
  const presentTopics = agentEventTopics(topics)
  return Object.keys(agentTopicList).map((topic) => {
    const allActions = agentTopicList[topic]
    const presentActions = presentTopics[topic]
    const actionElements = allActions.map((action) => {
      const wild = presentActions && presentActions.includes('**')
      const isPresent = wild || (presentActions && presentActions.includes(action))

      return <li key={`${topic}:${action}`}>{isPresent ? `${topic}:${action}` : <s>{`${topic}:${action}`}</s>}</li>
    })

    const isActive = !!presentTopics[topic]

    return (
      <Tooltip key={topic}>
        <TooltipTrigger asChild>
          <Button
            variant='outline'
            size='icon-md'
            className={`${isActive ? 'text-primary border-primary' : 'text-gray-400 border-gray-400'}`}
            icon={iconMap[topic] || iconMap.default}
            onClick={() =>
              window.open(`https://flatfile.com/docs/orchestration/events#${topic}`, '_blank', 'noopener,noreferrer')
            }
          />
        </TooltipTrigger>
        <TooltipContent className='flex items-center px-2 py-1'>
          <div className='p-2'>
            <Text variant='body' emphasized className='text-sm'>
              {topic}
            </Text>
            <div className='list-none p-0 text-sm'>
              <ul className='list-none p-0 m-0'>{actionElements}</ul>
              <Text variant='body' className='text-sm block mt-2'>
                Click to learn more
              </Text>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    )
  })
}
